import React, { useEffect, useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import { API_URL } from '../config/constants';
import Modal from './Modal';
import { AuthContext } from '../context/AuthContext';

interface Order {
  _id: string;
  orderNumber: string;
  orderDate: string;
  terminDate: string;
  manufacturerCompany: string;
  modelName: string;
  totalOrderQuantity: number;
  totalAmount: number;
  status: string;
  isArchived?: boolean;
}

interface ModalConfig {
  isOpen: boolean;
  title: string;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  onConfirm?: () => void;
}

interface ModalButton {
  text: string;
  onClick: () => void;
  className: string;
}

const OrderList: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [modal, setModal] = useState<ModalConfig>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });

  const { hasPermission, isAdmin } = useContext(AuthContext);

  const showModal = (title: string, message: string, type: 'success' | 'error' | 'info' | 'warning', onConfirm?: () => void) => {
    setModal({
      isOpen: true,
      title,
      message,
      type,
      onConfirm
    });
  };

  const closeModal = () => {
    setModal(prev => ({ ...prev, isOpen: false }));
    setSelectedOrder(null);
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      console.log('Arşiv durumu:', showArchived); // Arşiv durumunu kontrol et
      const response = await fetch(`${API_URL}/orders?isArchived=${showArchived.toString()}`);
      if (!response.ok) throw new Error('Siparişler yüklenemedi');
      const data = await response.json();
      console.log('API endpoint:', `${API_URL}/orders?isArchived=${showArchived.toString()}`); // API endpoint'ini kontrol et
      console.log('Gelen siparişler:', data); // Gelen verileri kontrol et
      console.log('Gelen sipariş sayısı:', data.length); // Gelen sipariş sayısını kontrol et
      setOrders(data);
    } catch (error) {
      console.error('Siparişler yüklenirken hata oluştu:', error);
      showModal('Hata', 'Siparişler yüklenirken bir hata oluştu', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveOrder = async (order: Order) => {
    try {
      const response = await fetch(`${API_URL}/orders/${order._id}/archive`, {
        method: 'PUT',
      });
      
      if (!response.ok) throw new Error('Sipariş arşivlenemedi');
      
      await fetchOrders();
      showModal('Başarılı', 'Sipariş başarıyla arşivlendi', 'success');
    } catch (error) {
      console.error('Arşivleme hatası:', error);
      showModal('Hata', 'Sipariş arşivlenirken bir hata oluştu', 'error');
    }
  };

  const handleUnarchiveOrder = async (order: Order) => {
    try {
      const response = await fetch(`${API_URL}/orders/${order._id}/unarchive`, {
        method: 'PUT',
      });
      
      if (!response.ok) throw new Error('Sipariş arşivden çıkarılamadı');
      
      await fetchOrders();
      showModal('Başarılı', 'Sipariş başarıyla arşivden çıkarıldı', 'success');
    } catch (error) {
      console.error('Arşivden çıkarma hatası:', error);
      showModal('Hata', 'Sipariş arşivden çıkarılırken bir hata oluştu', 'error');
    }
  };

  const handleDeleteOrder = async (order: Order) => {
    try {
      const response = await fetch(`${API_URL}/orders/${order._id}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) throw new Error('Sipariş silinemedi');
      
      await fetchOrders();
      showModal('Başarılı', 'Sipariş başarıyla silindi', 'success');
    } catch (error) {
      console.error('Silme hatası:', error);
      showModal('Hata', 'Sipariş silinirken bir hata oluştu', 'error');
    }
  };

  const confirmArchive = (order: Order) => {
    setSelectedOrder(order);
    showModal(
      'Siparişi Arşivle',
      `"${order.orderNumber}" numaralı siparişi arşivlemek istediğinizden emin misiniz?`,
      'warning',
      () => handleArchiveOrder(order)
    );
  };

  const confirmUnarchive = (order: Order) => {
    setSelectedOrder(order);
    showModal(
      'Siparişi Arşivden Çıkar',
      `"${order.orderNumber}" numaralı siparişi arşivden çıkarmak istediğinizden emin misiniz?`,
      'warning',
      () => handleUnarchiveOrder(order)
    );
  };

  const confirmDelete = (order: Order) => {
    setSelectedOrder(order);
    showModal(
      'Siparişi Sil',
      `"${order.orderNumber}" numaralı siparişi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`,
      'warning',
      () => handleDeleteOrder(order)
    );
  };

  useEffect(() => {
    fetchOrders();
  }, [showArchived]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-light text-gray-800">
            {showArchived ? 'Arşivlenmiş Siparişler' : 'Aktif Siparişler'}
          </h1>
          <button
            onClick={() => setShowArchived(!showArchived)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md border border-gray-300 transition-colors ${
              showArchived
                ? 'bg-white text-gray-700 hover:border-gray-400'
                : 'bg-white text-gray-700 hover:border-gray-400'
            }`}
          >
            {showArchived ? (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                </svg>
                <span>Ana Liste</span>
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z" />
                  <path fillRule="evenodd" d="M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" clipRule="evenodd" />
                </svg>
                <span>Arşiv</span>
              </>
            )}
          </button>
        </div>
        
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Siparişler</h1>
          
          {/* Sadece create izni olanlar yeni sipariş ekleyebilir */}
          {(isAdmin || hasPermission('orders', 'create')) && (
            <Link to="/create-order" className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded inline-flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 3a1 1 0 00-1 1v5H4a1 1 0 100 2h5v5a1 1 0 102 0v-5h5a1 1 0 100-2h-5V4a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              Yeni Sipariş
            </Link>
          )}
        </div>

        <div className="bg-white shadow-lg rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sipariş No
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tarih
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Termin Tarihi
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Üretici
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Model
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Miktar
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tutar
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Durum
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {orders.map((order) => (
                  <tr key={order._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {order.orderNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(order.orderDate).toLocaleDateString('tr-TR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(order.terminDate).toLocaleDateString('tr-TR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {order.manufacturerCompany}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {order.modelName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {order.totalOrderQuantity}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {order.totalAmount.toLocaleString('tr-TR')} ₺
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                        ${order.status === 'Beklemede' ? 'bg-yellow-100 text-yellow-800' : 
                          order.status === 'Üretimde' ? 'bg-blue-100 text-blue-800' : 
                          order.status === 'Tamamlandı' ? 'bg-green-100 text-green-800' : 
                          'bg-gray-100 text-gray-800'}`}>
                        {order.status || 'Beklemede'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex space-x-4">
                        <Link
                          to={`/order-detail/${order.orderNumber}`}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Detaylar
                        </Link>
                        {showArchived ? (
                          <button
                            onClick={() => confirmUnarchive(order)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Arşivden Çıkar
                          </button>
                        ) : (
                          <button
                            onClick={() => confirmArchive(order)}
                            className="text-yellow-600 hover:text-yellow-900"
                          >
                            Arşivle
                          </button>
                        )}
                        {(isAdmin || hasPermission('orders', 'delete')) && (
                          <button
                            onClick={() => confirmDelete(order)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Sil
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        buttons={(() => {
          const buttons: ModalButton[] = [{
            text: 'İptal',
            onClick: closeModal,
            className: 'bg-gray-500 hover:bg-gray-600 text-white'
          }];

          if (modal.onConfirm) {
            buttons.push({
              text: modal.type === 'warning' ? 'Arşivle' : 'Tamam',
              onClick: () => {
                modal.onConfirm?.();
                closeModal();
              },
              className: modal.type === 'warning'
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            });
          }

          return buttons;
        })()}
      />
    </div>
  );
};

export default OrderList;